import type { ReactNode, MouseEvent } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared sticky-layout modal primitive (ported from aieweb, PAR-6843).
 *
 * Compound component that owns ONE layout contract: the panel is height-capped
 * and column-flexed, the header and footer are pinned (shrink-0) with divider
 * borders, and only the body scrolls (flex-1 overflow-y-auto).
 *
 * LAYOUT-ONLY. This primitive deliberately adds NO new behavior:
 *  - no Escape handling (callers keep their own keydown effect)
 *  - no backdrop-click dismissal unless the caller passes onOverlayClick
 *  - no focus trap, no body-scroll-lock, no portal
 * Callers keep their own open/close state, dirty-checks, and submit logic.
 */

const MAX_WIDTH_CLASS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
} as const;

export type ModalMaxWidth = keyof typeof MAX_WIDTH_CLASS;

interface ModalProps {
  /** When false, nothing renders (matches existing `{isOpen && ...}` callers). */
  open: boolean;
  /** Optional overlay-click handler. Wire to the caller's existing close/dirty-check
   *  handler to preserve current backdrop behavior. Omit to disable backdrop dismissal. */
  onOverlayClick?: (e: MouseEvent<HTMLDivElement>) => void;
  /** Extra classes for the overlay (e.g. a different backdrop tint or blur). */
  overlayClassName?: string;
  children: ReactNode;
}

/**
 * Root: renders the centered, full-viewport overlay. Returns null when closed.
 * The overlay's onClick fires onOverlayClick ONLY when the click target is the
 * overlay itself (not a bubbled click from inside the panel). It does NOT render
 * the panel — wrap your content in <Modal.Content> for that.
 */
export function Modal({
  open,
  onOverlayClick,
  overlayClassName = "",
  children,
}: ModalProps) {
  if (!open) return null;

  return (
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", overlayClassName)}
      onClick={(e) => {
        // Only treat clicks on the overlay backdrop itself as dismissal intent.
        if (e.target === e.currentTarget) onOverlayClick?.(e);
      }}
    >
      {children}
    </div>
  );
}

interface ContentProps {
  /** Panel max width. Defaults to "lg". */
  maxWidth?: ModalMaxWidth;
  /** Extra classes for the panel (e.g. a custom rounded/border/bg, or extra mx-*).
   *  Comes last in the class list so caller overrides win (rounded-xl,, …). */
  className?: string;
  /** ARIA role for the panel. Defaults to "dialog"; pass "alertdialog" for
   *  destructive confirmations. */
  role?: string;
  /** ID of the element labelling the dialog (usually the heading). Forwarded
   *  to the panel as aria-labelledby. */
  "aria-labelledby"?: string;
  /** Accessible label when there is no visible heading element to reference. */
  "aria-label"?: string;
  children: ReactNode;
}

/**
 * Panel: the height-capped, column-flexed container that holds Header/Body/Footer.
 * This is the box that pins the header + footer and lets only the body scroll.
 * stopPropagation so a click inside the panel never bubbles to the overlay's
 * onOverlayClick.
 *
 * Carries dialog semantics by default: role="dialog" + aria-modal="true" so every
 * primitive dialog is announced as a modal dialog without each caller re-declaring it.
 * Callers supply the label via aria-labelledby (preferred — point at the heading's id)
 * or aria-label.
 */
function ModalContent({
  maxWidth = "lg",
  className = "",
  role = "dialog",
  "aria-labelledby": ariaLabelledby,
  "aria-label": ariaLabel,
  children,
}: ContentProps) {
  return (
    <div
      role={role}
      aria-modal="true"
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      className={cn("flex max-h-[90vh] w-full", MAX_WIDTH_CLASS[maxWidth], "flex-col rounded-lg bg-card shadow-xl", className)}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

interface SlotProps {
  className?: string;
  children: ReactNode;
}

/** Pinned header. Caller supplies inner layout (flex/padding) via className + children. */
function ModalHeader({ className = "", children }: SlotProps) {
  return (
    <div className={cn("shrink-0 border-b border-border", className)}>
      {children}
    </div>
  );
}

/** Scrolling body. The ONLY region that scrolls. Caller supplies padding via className. */
function ModalBody({ className = "", children }: SlotProps) {
  return (
    <div className={cn("flex-1 overflow-y-auto", className)}>
      {children}
    </div>
  );
}

/** Pinned footer. Caller supplies inner layout (flex/justify/padding) via className. */
function ModalFooter({ className = "", children }: SlotProps) {
  return (
    <div className={cn("shrink-0 border-t border-border", className)}>
      {children}
    </div>
  );
}

Modal.Content = ModalContent;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
